package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.DriveBoardSnapshot
import com.dotdrives.model.DriveSnapshotRequest
import com.dotdrives.model.DrivePhaseStats
import com.dotdrives.model.DriveFinancialTotals
import kotlin.time.Duration.Companion.days
import com.dotdrives.model.DrivePhase
import com.dotdrives.model.DrivePhaseTemplate
import com.dotdrives.model.DriveFinancials
import com.dotdrives.model.DriveSummary
import com.dotdrives.model.Target
import com.dotdrives.repository.DriveRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.math.ceil
import kotlin.time.Clock

enum class DrivesView {
    Active,
    Completed,
}

data class DriveFilters(
    val stewardUserId: String? = null,
    val targetId: String? = null,
    val tag: String? = null,
    val giverType: String? = null,
)

data class DrivesBoardUiState(
    val loading: Boolean = false,
    val templates: List<DrivePhaseTemplate> = emptyList(),
    val selectedTemplateId: String? = null,
    val phases: List<DrivePhase> = emptyList(),
    val targets: List<Target> = emptyList(),
    val drives: List<DriveSummary> = emptyList(),
    val financials: Map<String, DriveFinancials> = emptyMap(),
    val phaseStats: List<DrivePhaseStats> = emptyList(),
    val totalCount: Long = 0,
    val availableTags: List<String> = emptyList(),
    val financialTotals: DriveFinancialTotals? = null,
    val loadingPhases: Set<String> = emptySet(),
    val exporting: Boolean = false,
    val historyStartDate: String? = (Clock.System.now() - 90.days).toString().take(10),
    val historyEndDate: String? = null,
    val completedFinancialTotals: DriveFinancialTotals? = null,
    val query: String = "",
    val filters: DriveFilters = DriveFilters(),
    val view: DrivesView = DrivesView.Active,
    val completed: List<DriveSummary> = emptyList(),
    val completedFinancials: Map<String, DriveFinancials> = emptyMap(),
    val completedPage: Int = 1,
    val completedTotalCount: Long = 0,
    val completedLoading: Boolean = false,
    val activeError: String? = null,
    val completedError: String? = null,
    val configurationError: String? = null,
) {
    // Concurrent request families own their errors; display only this view's failure.
    val error: String?
        get() = configurationError ?: if (view == DrivesView.Active) activeError else completedError

    val completedTotalPages: Int
        get() = maxOf(1, ceil(completedTotalCount.toDouble() / COMPLETED_PAGE_SIZE).toInt())
}

class DrivesBoardController(
    private val repo: DriveRepository,
    scope: CoroutineScope,
    private val orgId: String,
) : DotController<DrivesBoardUiState>(DrivesBoardUiState(), scope) {
    private var searchJob: Job? = null
    private var boardJob: Job? = null
    private var completedJob: Job? = null
    private val pageJobs = mutableMapOf<String, Job>()
    private var boardGeneration = 0L
    private var completedGeneration = 0L

    fun load() {
        val generation = nextBoardGeneration()
        update { it.copy(loading = true, configurationError = null, activeError = null) }
        scope.launch {
            try {
                val templatesResult = repo.templates(orgId)
                val targetsResult = repo.targets(orgId)
                if (!isCurrentBoard(generation)) return@launch
                templatesResult
                    .onSuccess { templates ->
                        val targets = targetsResult.getOrNull().orEmpty()
                        val selected = state.value.selectedTemplateId
                            ?.takeIf { id -> templates.any { it.id == id } }
                            ?: templates.firstOrNull(DrivePhaseTemplate::isDefault)?.id
                            ?: templates.firstOrNull()?.id
                        if (selected != state.value.selectedTemplateId) clearProcessRows(selected)
                        update { it.copy(templates = templates, targets = targets, selectedTemplateId = selected) }
                        if (state.value.view == DrivesView.Completed && selected != null) loadCompletedPage(1)
                        if (selected == null) {
                            update { it.copy(loading = false, phases = emptyList(), drives = emptyList()) }
                        } else {
                            loadBoard(selected, generation)
                        }
                    }
                    .onFailure { publishConfigurationError(it, generation) }
            } catch (error: Exception) {
                publishConfigurationError(error, generation)
            }
        }
    }

    fun selectTemplate(templateId: String) {
        searchJob?.cancel()
        val generation = nextBoardGeneration()
        clearProcessRows(templateId)
        loadBoard(templateId, generation)
        if (state.value.view == DrivesView.Completed) loadCompletedPage(1)
    }

    private fun clearProcessRows(templateId: String?) {
        nextCompletedGeneration()
        update {
            it.copy(
                selectedTemplateId = templateId,
                phases = emptyList(), drives = emptyList(), financials = emptyMap(),
                phaseStats = emptyList(), totalCount = 0, financialTotals = null, availableTags = emptyList(),
                completed = emptyList(), completedFinancials = emptyMap(),
                completedPage = 1, completedTotalCount = 0, completedLoading = false,
                activeError = null, completedError = null, configurationError = null,
            )
        }
    }

    fun search(query: String) {
        searchJob?.cancel()
        val generation = nextBoardGeneration()
        update { it.copy(query = query, drives = emptyList(), phaseStats = emptyList(), financialTotals = null, totalCount = 0, activeError = null) }
        val templateId = state.value.selectedTemplateId ?: return
        searchJob = scope.launch {
            delay(SEARCH_DEBOUNCE_MILLIS)
            loadBoard(templateId, generation)
        }
    }

    fun setFilter(filters: DriveFilters) {
        searchJob?.cancel()
        val generation = nextBoardGeneration()
        update { it.copy(filters = filters, drives = emptyList(), phaseStats = emptyList(), financialTotals = null, totalCount = 0, activeError = null) }
        val templateId = state.value.selectedTemplateId ?: return
        loadBoard(templateId, generation)
    }

    fun setView(view: DrivesView) {
        update { it.copy(view = view) }
        if (view == DrivesView.Completed && state.value.completed.isEmpty() && !state.value.completedLoading) {
            loadCompletedPage(1)
        }
    }

    fun goToCompletedPage(n: Int) {
        val page = n.coerceIn(1, state.value.completedTotalPages)
        if (page == state.value.completedPage && state.value.completed.isNotEmpty()) return
        loadCompletedPage(page)
    }

    fun retry() {
        val current = state.value
        if (current.view == DrivesView.Active || current.selectedTemplateId == null || current.configurationError != null) {
            load()
        } else {
            loadCompletedPage(current.completedPage)
        }
    }

    fun movePhase(driveId: String, toPhaseId: String, outcome: String? = null) {
        val current = state.value
        val drive = current.drives.firstOrNull { it.id == driveId } ?: return
        val toPhase = current.phases.firstOrNull { it.id == toPhaseId } ?: return
        val fromPhase = current.phases.firstOrNull { it.id == drive.currentPhaseId }
        val fromPhaseId = fromPhase?.id ?: drive.currentPhaseId
        val fromPhaseName = fromPhase?.name ?: ""
        val previousDrives = current.drives
        val movedDrive = drive.copy(
            currentPhaseId = toPhaseId,
            outcome = outcome,
            phaseEnteredAt = Clock.System.now(),
        )
        update {
            it.copy(
                drives = if (outcome != null) {
                    it.drives.filterNot { d -> d.id == driveId }
                } else {
                    it.drives.map { d -> if (d.id == driveId) movedDrive else d }
                },
                activeError = null,
            )
        }
        scope.launch {
            repo.movePhase(orgId, driveId, fromPhaseId, fromPhaseName, toPhaseId, toPhase.name, outcome)
                .onFailure { error ->
                    if (error is CancellationException) throw error
                    update {
                        it.copy(
                            drives = previousDrives,
                            activeError = error.message ?: "Something went wrong",
                        )
                    }
                }
        }
    }

    private fun boardRequest(templateId: String): DriveSnapshotRequest = state.value.let {
        DriveSnapshotRequest(templateId, it.query.trim().takeIf(String::isNotEmpty),
            it.filters.stewardUserId, it.filters.targetId, it.filters.tag, it.filters.giverType)
    }

    private fun loadBoard(templateId: String, generation: Long) {
        if (!isCurrentBoard(generation)) return
        update { it.copy(loading = true, activeError = null) }
        val request = boardRequest(templateId)
        boardJob = scope.launch {
            try {
                val phases = repo.phases(orgId, templateId).getOrThrow()
                val result = repo.snapshot(orgId, request).getOrThrow()
                if (!isCurrentBoard(generation)) return@launch
                update { it.copy(loading = false, phases = phases, drives = result.cards,
                    financials = result.financials.associateBy(DriveFinancials::driveId),
                    phaseStats = result.phaseStats, totalCount = result.totalCount,
                    financialTotals = result.financialTotals, availableTags = result.tags, activeError = null) }
            } catch (error: Exception) { publishBoardError(error, generation) }
        }
    }

    fun loadMore(phaseId: String) {
        val current = state.value
        val templateId = current.selectedTemplateId ?: return
        val loaded = current.drives.count { it.currentPhaseId == phaseId }
        val total = current.phaseStats.firstOrNull { it.phaseId == phaseId }?.totalCount ?: return
        if (current.loading || phaseId in current.loadingPhases || loaded >= total) return
        val generation = boardGeneration
        val request = boardRequest(templateId).copy(phaseId = phaseId, offset = loaded)
        update { it.copy(loadingPhases = it.loadingPhases + phaseId, activeError = null) }
        pageJobs[phaseId] = scope.launch {
            try {
                val result = repo.snapshot(orgId, request).getOrThrow()
                if (!isCurrentBoard(generation)) return@launch
                update { it.copy(drives = (it.drives + result.cards).distinctBy(DriveSummary::id),
                    financials = it.financials + result.financials.associateBy(DriveFinancials::driveId),
                    phaseStats = result.phaseStats, totalCount = result.totalCount,
                    financialTotals = result.financialTotals, loadingPhases = it.loadingPhases - phaseId) }
            } catch (error: Exception) {
                publishBoardError(error, generation)
                if (isCurrentBoard(generation)) update { it.copy(loadingPhases = it.loadingPhases - phaseId) }
            }
        }
    }

    fun setHistoryDates(startDate: String?, endDate: String?) {
        update { it.copy(historyStartDate = startDate, historyEndDate = endDate,
            completed = emptyList(), completedTotalCount = 0, completedPage = 1, completedFinancialTotals = null) }
        loadCompletedPage(1)
    }

    fun exportAll(onReady: (List<DriveSummary>, List<DrivePhase>) -> Unit) {
        val current = state.value
        val template = current.selectedTemplateId ?: return
        if (current.exporting) return
        val request = boardRequest(template)
        val generation = boardGeneration
        update { it.copy(exporting = true) }
        scope.launch {
            try {
                // Export explicitly fetches every matching page, independently of rendered cards.
                val rows = mutableListOf<DriveSummary>()
                for (phase in current.phases) {
                    var offset = 0
                    do {
                        if (!isCurrentBoard(generation)) return@launch
                        val page = repo.snapshot(orgId, request.copy(phaseId = phase.id, offset = offset)).getOrThrow()
                        rows += page.cards
                        offset += page.cards.size
                    } while (page.cards.size == BOARD_PAGE_SIZE)
                }
                if (isCurrentBoard(generation)) onReady(rows.distinctBy(DriveSummary::id), current.phases)
            } catch (error: Exception) { publishBoardError(error, generation) }
            finally { update { it.copy(exporting = false) } }
        }
    }

    private fun loadCompletedPage(page: Int) {
        val current = state.value
        val templateId = current.selectedTemplateId ?: return
        val generation = nextCompletedGeneration()
        val request = DriveSnapshotRequest(templateId, closed = true, offset = (page - 1) * COMPLETED_PAGE_SIZE,
            startDate = current.historyStartDate, endDate = current.historyEndDate)
        update { it.copy(completedLoading = true, completedError = null) }
        completedJob = scope.launch {
            try {
                val result = repo.snapshot(orgId, request).getOrThrow()
                if (!isCurrentCompleted(generation)) return@launch
                update { it.copy(completedLoading = false, completed = result.cards,
                    completedFinancials = result.financials.associateBy(DriveFinancials::driveId),
                    completedFinancialTotals = result.financialTotals,
                    completedPage = page, completedTotalCount = result.totalCount, completedError = null) }
            } catch (error: Exception) {
                if (error is CancellationException) throw error
                if (isCurrentCompleted(generation)) update { it.copy(completedLoading = false, completedError = error.message ?: "Something went wrong") }
            }
        }
    }

    private fun publishConfigurationError(error: Throwable, generation: Long) {
        if (error is CancellationException) throw error
        if (!isCurrentBoard(generation)) return
        update { it.copy(loading = false, configurationError = error.message ?: "Something went wrong") }
    }

    private fun publishBoardError(error: Throwable, generation: Long) {
        if (error is CancellationException) throw error
        if (!isCurrentBoard(generation)) return
        update {
            if (isCurrentBoard(generation)) {
                it.copy(loading = false, activeError = error.message ?: "Something went wrong")
            } else {
                it
            }
        }
    }

    private fun nextBoardGeneration(): Long {
        boardJob?.cancel()
        pageJobs.values.forEach(Job::cancel)
        pageJobs.clear()
        update { it.copy(loadingPhases = emptySet()) }
        boardGeneration += 1
        return boardGeneration
    }

    private fun isCurrentBoard(generation: Long): Boolean = generation == boardGeneration

    private fun nextCompletedGeneration(): Long {
        completedJob?.cancel()
        completedGeneration += 1
        return completedGeneration
    }

    private fun isCurrentCompleted(generation: Long): Boolean = generation == completedGeneration
}

private const val SEARCH_DEBOUNCE_MILLIS = 250L
const val BOARD_PAGE_SIZE = 50
const val COMPLETED_PAGE_SIZE = 50
